import dayjs from './dayjs';

/**
 * Util bersama untuk menghitung periode tampilan buku kas
 * (dipakai oleh Transaction.js dan Statistic.js).
 *
 * Satu sumber kebenaran untuk logika start/end/title, supaya
 * kedua halaman tidak mungkin berbeda perilaku lagi.
 */

const listDay = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

/**
 * Set hari pertama minggu sesuai pengaturan buku.
 * Dipanggil di useEffect halaman, karena dayjs menyimpan
 * locale secara global dan halaman bisa dibuka langsung (refresh).
 */
export const applyWeeklyStart = (weekly_start) => {
    dayjs.updateLocale('en', {
        weekStart: listDay.indexOf(weekly_start) >= 0 ? listDay.indexOf(weekly_start) : 1
    });
};

/**
 * Hitung periode aktif beserta judulnya.
 *
 * @param {Object} book   - objek buku dari BookStore (display, monthly_start, dll)
 * @param {dayjs} date    - tanggal acuan saat ini (state `date` halaman)
 * @param {number} add    - offset navigasi: -1 (prev), 0 (awal), 1 (next)
 *
 * @returns {{start: string, end: string, title: string, date: dayjs}}
 */
export const hitungPeriode = (book, date, add) => {
    let start, end, title;

    if (book.display === 'daily') {
        const d = date.clone().add(add, 'day');
        start = d.format('YYYY-MM-DD');
        end = start;
        title = d.format('D MMM YYYY');
        return { start, end, title, date: d };
    }

    if (book.display === 'weekly') {
        const d = date.clone().add(add, 'week');
        start = d.clone().startOf('week').format('YYYY-MM-DD');
        end = d.clone().endOf('week').format('YYYY-MM-DD');
        title = `${dayjs(start).format('D MMM')} - ${dayjs(end).format('D MMM')}`;
        return { start, end, title, date: d };
    }

    if (book.display === 'monthly') {
        // anchor ke tanggal mulai siklus. daysInMonth() mencegah overflow
        // saat monthly_start > jumlah hari bulan tsb (mis. 31 di Feb).
        const d = date.clone()
            .add(add, 'month')
            .date(Math.min(book.monthly_start, date.clone().add(add, 'month').daysInMonth()));
        // jika tanggal hari ini masih sebelum tanggal mulai siklus,
        // maka periode berjalan adalah siklus bulan sebelumnya
        // (misal: mulai tgl 15, hari ini tgl 10 -> periode 15 bln lalu s/d 14 bln ini)
        let periode = d.clone();
        if (dayjs().date() < book.monthly_start) {
            periode = periode.add(-1, 'month');
        }
        start = periode.clone().format('YYYY-MM-DD');
        end = periode.clone().add(1, 'month').add(-1, 'day').format('YYYY-MM-DD');
        // judul mengikuti bulan dominan: siklus yang mulai akhir bulan
        // (mulai 18-28) lebih dekat ke bulan berikutnya
        title = periode.clone().add(book.monthly_start > 17 ? 1 : 0, 'month').format('MMM YYYY');
        return { start, end, title, date: d };
    }

    if (book.display === 'yearly') {
        const d = date.clone().add(add, 'year').month(book.yearly_start).date(1);
        start = d.clone().startOf('month').format('YYYY-MM-DD');
        end = d.clone().add(1, 'year').add(-1, 'month').endOf('month').format('YYYY-MM-DD');
        title = d.format('YYYY');
        return { start, end, title, date: d };
    }

    // custom
    start = book.custom_start;
    end = book.custom_end;
    title = dayjs(start).format("D MMM 'YY") + ' - ' + dayjs(end).format("D MMM 'YY");
    return { start, end, title, date: null };
};
